import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export function PageHeader({ title, subtitle, actions, icon: Icon }) {
  return (
    <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div className="flex items-center gap-2.5">
          {Icon && (
            <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-700/60 bg-slate-800/50 text-blue-400">
              <Icon className="h-4.5 w-4.5" size={18} />
            </span>
          )}
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">{title}</h1>
        </div>
        {subtitle && <p className="mt-2 max-w-2xl text-sm text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, desc, actions, children, className, testid }) {
  return (
    <section data-testid={testid} className={cn("rounded-xl border border-slate-800 bg-slate-900/50", className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-slate-800 px-5 py-3.5">
          <div>
            {title && <h3 className="font-display text-sm font-semibold tracking-wide text-slate-100">{title}</h3>}
            {desc && <p className="mt-0.5 text-xs text-slate-500">{desc}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Loader({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500" data-testid="loader">
      <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> {label}…
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", desc, icon: Icon, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-800 py-16 text-center">
      {Icon && <Icon className="h-8 w-8 text-slate-600" />}
      <div>
        <p className="font-display text-sm font-semibold text-slate-300">{title}</p>
        {desc && <p className="mt-1 text-xs text-slate-500">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

export function Stat({ label, value, sub, tone = "slate", className }) {
  const tones = {
    slate: "text-slate-50", blue: "text-blue-400", emerald: "text-emerald-400",
    amber: "text-amber-400", red: "text-red-400", cyan: "text-cyan-300", purple: "text-purple-400",
  };
  return (
    <div className={cn("rounded-xl border border-slate-800 bg-slate-900/50 p-4", className)}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={cn("mt-1.5 font-display text-2xl font-bold tabular-nums", tones[tone])}>{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}
